import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScoreLocalService } from '../../pages/score/score.service';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { environment } from '../../../environments/environment';
import { ExternalLinkService } from '../../services/external-link.service';
import { NavigationTrackingService, NavigationTriggerType } from '../../services/navigation-tracking.service';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, AngularSvgIconModule],
})
export class SideNavComponent implements OnDestroy {
  private subscriptions = new Subscription();
  private activeRoute: string;

  // Display-only; already carries the "v" prefix (e.g. "v 2.1.84").
  version: string = environment.version;

  constructor(
    private router: Router,
    private scoreLocalService: ScoreLocalService,
    private externalLink: ExternalLinkService,
    private navigationTracking: NavigationTrackingService,
  ) {
    this.activeRoute = this.currentPath();
    const sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activeRoute = this.currentPath();
      });
    this.subscriptions.add(sub);
  }

  private currentPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  isRouteActive(path: string, exact = false): boolean {
    return exact ? this.activeRoute === path : this.activeRoute === path || this.activeRoute.startsWith(`${path}/`);
  }

  /** Fires a SideNav*Clicked NavigationEvent. `toScreen` is the routerLink target path. */
  trackNavItem(toScreen: string, eventName: string): void {
    this.navigationTracking.track({
      fromScreen: this.activeRoute,
      toScreen,
      triggerType: NavigationTriggerType.ViaNavigation,
      eventName,
    });
  }

  navigateScore(): void {
    const fromScreen = this.activeRoute;
    const sub = this.scoreLocalService.getLastScore().subscribe(() => {
      const route = this.scoreLocalService.shouldShowMarginOfSafety()
        ? '/score/marginofsafety'
        : '/score';
      this.navigationTracking.track({
        fromScreen,
        toScreen: route,
        triggerType: NavigationTriggerType.ViaNavigation,
        eventName: 'SideNavScoreClicked',
      });
      this.router.navigate([route]);
    });
    this.subscriptions.add(sub);
  }

  isScoreActive(): boolean {
    return this.router.url.startsWith('/score') || this.isRouteActive('/history');
  }

  isHomeActive(): boolean {
    return this.isRouteActive('/landing-page', true) || this.isRouteActive('/brainbites');
  }

  isPlanActive(): boolean {
    return this.isRouteActive('/plan-home') || this.isRouteActive('/plan') || this.isRouteActive('/card-library');
  }

  openExternalLink(event: Event, url: string): void {
    event.preventDefault();
    this.externalLink.open(url);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
